<template>
    <div
        class="widget__route-planning"
        :class="{
            opened: isMobile && mobileSearch,
            bannerOffset: downloadBannerVisible,
        }"
    >
        <div>
            <!-- Loading indicator -->
            <div class="loading-indicator" v-if="loading">
                <div class="text-center text-white">
                    <div class="lds-ring">
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                    </div>
                    <p class="mb-0">Searching for routes…</p>
                </div>
            </div>
            <svg class="widget__route-planning__burger" @click="opensidebar()">
                <use :xlink:href="`${icons['hamburger']}#icon`"></use>
            </svg>
            <h3>Route Planning</h3>
            <div class="widget__route-planning__content">
                <div class="d-flex align-items-center">
                    <div
                        class="d-flex flex-column align-self-stretch justify-content-stretch"
                        id="widget__route-planning__points"
                        style="flex: 1 0 auto; max-width: 32px"
                    >
                        <!-- Left aside -->
                        <div
                            class="flex-fill d-flex align-items-center color--blue"
                        >
                            <svg class="widget__route-planning__icon">
                                <use
                                    :xlink:href="`${icons['origin']}#icon`"
                                ></use>
                            </svg>
                        </div>
                        <div
                            class="flex-fill d-flex align-items-center color--blue"
                            v-for="waypoint in route.waypoints"
                            :key="`icon-${waypoint.name}`"
                        >
                            <svg class="widget__route-planning__icon">
                                <use
                                    :xlink:href="`${icons['origin']}#icon`"
                                ></use>
                            </svg>
                        </div>
                        <div
                            class="flex-fill d-flex align-items-center color--orange"
                        >
                            <svg
                                class="widget__route-planning__icon icon--scaled"
                            >
                                <use
                                    :xlink:href="`${icons['place']}#icon`"
                                ></use>
                            </svg>
                        </div>
                    </div>
                    <div class="flex-fill">
                        <!-- Main -->
                        <input-search
                            v-model="route.origin.name"
                            :placeholder="'Choose starting point'"
                            @search="findSearch(route.origin, 'origin')"
                            @keyup.enter.native="
                                pickSuggestionFromList('origin')
                            "
                            @input="
                                showSuggestions('origin');
                                trackGoogle('PLAN_SEARCH_OPEN');
                            "
                            @focusin.native="
                                showSuggestions('origin');
                                trackGoogle('PLAN_STARTING_POINT_FOCUS');
                            "
                            @focusout.native="hideSuggestions('origin')"
                            @keyup.arrow-down.native="
                                listSuggestions('origin', 1)
                            "
                            @keyup.arrow-up.native="
                                listSuggestions('origin', -1)
                            "
                            ref="originInput"
                        ></input-search>
                        <!-- Waypoints -->
                        <input-search
                            v-for="(waypoint, index) in route.waypoints"
                            v-model="route.waypoints[index].name"
                            :key="`waypoint-input-${index}`"
                            @input="
                                waypointIndex = index;
                                waypointsAutocomplete(index);
                            "
                            @focusin.native="waypointIndex = index"
                        ></input-search>
                        <input-search
                            v-model="route.destination.name"
                            :placeholder="'Choose destination point'"
                            @search="
                                findSearch(route.destination, 'destination')
                            "
                            @keyup.enter.native="
                                pickSuggestionFromList('destination')
                            "
                            @input="
                                showSuggestions('destination');
                                trackGoogle('PLAN_SEARCH_OPEN');
                            "
                            @focusin.native="
                                showSuggestions('destination');
                                trackGoogle('PLAN_DESTINATION_POINT_FOCUS');
                            "
                            @focusout.native="hideSuggestions('destination')"
                            @keyup.arrow-down.native="
                                listSuggestions('destination', 1)
                            "
                            @keyup.arrow-up.native="
                                listSuggestions('destination', -1)
                            "
                        ></input-search>

                        <div
                            v-if="route.suggestions.showOrigin && !isMobile"
                            class="suggestions suggestions--origin"
                            @mouseleave="route.suggestions.showOrigin = false"
                        >
                            <div
                                v-for="(suggestion, index) in route.suggestions
                                    .origin"
                                :key="suggestion.id"
                                class="suggestion"
                                :class="{
                                    hovered:
                                        hoveredSuggestion.property ===
                                            'origin' &&
                                        hoveredSuggestion.key === index,
                                }"
                                @click="pickSuggestion(suggestion, 'origin')"
                            >
                                <h6>
                                    {{ suggestion.label }}
                                </h6>
                                <small
                                    ><span v-show="suggestion.address.state"
                                        >{{ suggestion.address.state }},
                                    </span>
                                    {{ suggestion.address.country }}</small
                                >
                            </div>
                        </div>
                        <div
                            v-if="
                                route.suggestions.showDestination && !isMobile
                            "
                            class="suggestions suggestions--destination"
                            @mouseleave="
                                route.suggestions.showDestination = false
                            "
                        >
                            <div
                                v-for="(suggestion, index) in route.suggestions
                                    .destination"
                                :key="suggestion.id"
                                class="suggestion"
                                @click="
                                    pickSuggestion(suggestion, 'destination')
                                "
                                :class="{
                                    hovered:
                                        hoveredSuggestion.property ===
                                            'destination' &&
                                        hoveredSuggestion.key === index,
                                }"
                            >
                                <h6>
                                    {{ suggestion.label }}
                                </h6>
                                <small
                                    ><span v-show="suggestion.address.state"
                                        >{{ suggestion.address.state }},
                                    </span>
                                    {{ suggestion.address.country }}</small
                                >
                            </div>
                        </div>
                        <div
                            v-if="waypointSuggestions"
                            class="suggestions"
                            :style="`top: ${waypointSuggestions.position}px`"
                            @mouseleave="waypointIndex = null"
                        >
                            <div
                                v-for="suggestion in waypointSuggestions.suggestions"
                                :key="suggestion.id"
                                class="suggestion"
                                @click="
                                    pickSuggestion(
                                        suggestion,
                                        '',
                                        waypointIndex,
                                    )
                                "
                            >
                                <h6>
                                    {{ suggestion.label }}
                                </h6>
                                <small
                                    ><span v-show="suggestion.address.state"
                                        >{{ suggestion.address.state }},
                                    </span>
                                    {{ suggestion.address.country }}</small
                                >
                            </div>
                        </div>
                    </div>
                    <!-- eslint-disable -->
                    <div
                        class="text-right d-flex flex-column align-self-stretch justify-content-stretch"
                        style="flex: 1 0 auto; max-width: 32px"
                    >
                        <!-- eslint-enable -->
                        <!-- Right side -->
                        <div
                            class="flex-fill d-flex align-items-center align-self-center"
                            v-if="route.waypoints.length"
                        >
                            <div style="height: 24px"></div>
                        </div>
                        <div
                            class="flex-fill d-flex align-items-center align-self-center"
                            v-for="(waypoint, index) in route.waypoints"
                            @click="removeWaypoint(index)"
                            :key="`remove-waypoint-${index}`"
                        >
                            <svg class="widget__route-planning__icon--control">
                                <use
                                    :xlink:href="`${icons['close']}#icon`"
                                ></use>
                            </svg>
                        </div>
                        <div
                            class="flex-fill d-flex align-items-center align-self-center"
                        >
                            <svg
                                class="widget__route-planning__icon--control"
                                @click="swapOriginDestination()"
                            >
                                <use
                                    :xlink:href="`${icons['import_export']}#icon`"
                                ></use>
                            </svg>
                        </div>
                    </div>
                </div>
                <!-- Add destination -->
                <div
                    class="d-flex align-items-center my-2"
                    v-if="showAddDestination"
                >
                    <div
                        class="d-flex flex-column align-self-stretch justify-content-stretch"
                        style="flex: 1 0 auto; max-width: 32px"
                    >
                        <div
                            class="flex-fill d-flex align-items-center"
                            style="color: #fff"
                        >
                            <svg
                                class="widget__route-planning__icon add-icon"
                                @click="addNewDestination()"
                            >
                                <use :xlink:href="`${icons['add']}#icon`"></use>
                            </svg>
                        </div>
                    </div>
                    <div class="flex-fill" style="color: rgba(78, 58, 48, 0.6)">
                        Add destination
                    </div>
                </div>
                <div v-else class="d-flex align-items-center">
                    <div
                        class="d-flex flex-column align-self-stretch justify-content-stretch"
                        style="flex: 1 0 auto; max-width: 32px"
                    ></div>
                    <div class="flex-fill">
                        <hr class="divider" style="border-color: transparent" />
                    </div>
                    <div
                        class="text-right"
                        style="flex: 1 0 auto; max-width: 32px"
                    ></div>
                </div>
                <div v-if="error.show" class="d-flex align-items-center">
                    <div
                        class="d-flex flex-column align-self-stretch justify-content-stretch"
                        style="flex: 1 0 auto; max-width: 32px"
                    ></div>
                    <div class="flex-fill alert alert-danger">
                        <strong>{{ error.msg }}</strong>
                    </div>
                    <div
                        class="text-right"
                        style="flex: 1 0 auto; max-width: 32px"
                    ></div>
                </div>

                <!-- Departure type -->
                <div v-if="false" class="d-flex widget__route-planning__row">
                    <div
                        class="d-flex align-items-center text-center"
                        style="flex: 1 0 auto; max-width: 32px"
                    >
                        <svg class="widget__route-planning__icon color--brown">
                            <use :xlink:href="`${icons['time']}#icon`"></use>
                        </svg>
                    </div>
                    <div class="flex-fill">
                        <input-drop
                            v-model="route.departure"
                            :options="options.departure"
                            :type="'seamless'"
                            :icon="'chevron-right-small'"
                        >
                        </input-drop>
                    </div>
                    <div
                        class="text-right"
                        style="flex: 1 0 auto; max-width: 32px"
                    ></div>
                </div>

                <!-- Departure picker -->
                <div>
                    <div
                        v-if="showDepartureDatepicker"
                        class="d-flex align-items-center"
                        style="padding-top: 16px"
                    >
                        <div
                            class="d-flex flex-column align-self-stretch justify-content-stretch"
                            style="
                                flex: 1 0 auto;
                                max-width: 32px;
                                min-width: 32px;
                            "
                        >
                            &nbsp;
                        </div>
                        <div class="flex-fill">
                            <div class="d-flex" style="gap: 12px">
                                <div class="flex-fill" style="max-width: 128px">
                                    <input-drop
                                        v-model="route.date"
                                        :options="options.date"
                                        :comparable="comparableDate"
                                        :type="'right'"
                                    ></input-drop>
                                </div>
                                <div class="flex-fill" style="max-width: 98px">
                                    <input-drop
                                        v-model="route.time"
                                        :options="options.time"
                                        :comparable="comparableTime"
                                    ></input-drop>
                                </div>
                            </div>
                        </div>
                        <div
                            class="text-right"
                            style="
                                flex: 1 0 auto;
                                max-width: 32px;
                                min-width: 32px;
                            "
                        >
                            &nbsp;
                        </div>
                    </div>
                </div>

                <!-- Truck preset -->
                <div
                    class="widget__route-planning__row truck-preset mb-2"
                    :class="{
                        'd-flex': !isMobile || (isMobile && mobileSearch),
                        'd-none': isMobile && !mobileSearch,
                    }"
                >
                    <div
                        class="d-flex align-items-center text-center"
                        style="flex: 1 0 auto; max-width: 32px"
                    >
                        <svg class="widget__route-planning__icon color--brown">
                            <use
                                :xlink:href="`${icons['truck-presets']}#icon`"
                            ></use>
                        </svg>
                    </div>
                    <div class="flex-fill">
                        <input-drop
                            v-model="route.truck_preset"
                            :options="options.truck_preset"
                            :type="'seamless-combined'"
                            :label="'Truck Presets'"
                            :icon="'chevron-right-small'"
                            @click.native.stop="showPresets"
                        >
                        </input-drop>
                    </div>
                    <div
                        class="text-right"
                        style="flex: 1 0 auto; max-width: 32px"
                    ></div>
                </div>

                <!-- Mobile suggestions -->
                <div>
                    <div
                        class="d-flex widget__route-planning__row mobile-suggestions mb-2"
                        @click="
                            currentLocationToRoutePlan(
                                mobileSearch.toLowerCase(),
                            )
                        "
                        v-if="isMobile && mobileSearch"
                    >
                        <div
                            class="d-flex align-items-center text-center"
                            style="flex: 1 0 auto; max-width: 32px"
                        >
                            <svg
                                class="widget__route-planning__icon color--brown"
                            >
                                <use
                                    :xlink:href="`${icons['mylocation']}#icon`"
                                ></use>
                            </svg>
                        </div>
                        <div class="flex-fill">
                            <div
                                style="
                                    height: 48px;
                                    line-height: 48px;
                                    font-size: 18px;
                                    padding-left: 12px;
                                "
                            >
                                Current location
                            </div>
                        </div>
                        <div
                            class="text-right"
                            style="flex: 1 0 auto; max-width: 32px"
                        ></div>
                    </div>
                    <div v-if="route.suggestions[`show${mobileSearch}`]">
                        <div
                            v-for="suggestion in route.suggestions[
                                `${mobileSearch.toLowerCase()}`
                            ]"
                            :key="suggestion.id"
                            class="suggestion d-flex widget__route-planning__row"
                            @click="
                                pickSuggestion(
                                    suggestion,
                                    mobileSearch.toLowerCase(),
                                )
                            "
                        >
                            <div
                                class="d-flex align-items-center text-center"
                                style="flex: 1 0 auto; max-width: 32px"
                            >
                                <small
                                    ><span v-show="suggestion.address.state"
                                        >{{ suggestion.address.state }},
                                    </span>
                                    {{ suggestion.address.country }}</small
                                >
                            </div>
                            <div
                                class="flex-fill d-flex align-items-center"
                                style="
                                    padding-left: 12px;
                                    font-size: 18px;
                                    min-height: 48px;
                                    height: auto;
                                "
                            >
                                <h6 class="mb-0">
                                    {{ suggestion.label }}
                                </h6>
                            </div>
                            <div
                                class="text-right"
                                style="flex: 1 0 32px; max-width: 32px"
                            ></div>
                        </div>
                    </div>
                </div>

                <!-- Send to mobile -->
                <div
                    v-if="!isMobile && hasRoute && route.duration"
                    class="widget__route-planning__row to-device mb-2 d-flex"
                >
                    <div
                        class="d-flex align-items-center text-center"
                        style="flex: 1 0 auto; max-width: 32px"
                    >
                        <!-- <svg class="widget__route-planning__icon color--blue">
                        <use :xlink:href="`${icons['share']}#icon`"></use>
                    </svg> -->
                    </div>
                    <div
                        class="flex-fill align-items-center d-flex justify-content-center"
                    >
                        <Button
                            @click.native="showRouteShare()"
                            :type="'seamless'"
                            class="blue"
                            :disabled="!hasRoute"
                        >
                            SHARE
                        </Button>
                    </div>
                    <div
                        class="text-right"
                        style="flex: 1 0 auto; max-width: 32px"
                    ></div>
                </div>

                <!--
            <div v-if="overview.pois && overview.pois.length > 0">
                <div v-for="poi in overview.pois" :key="poi.guid">
                    {{ poi.title }}
                    {{ poi.distance.toFixed(2) }}
                    {{ poi.distance_on_route.toFixed(2) }}
                </div>
            </div>
            --></div>
            <div
                class="flex-fill"
                @click="$emit('setMobileSearch', null)"
            ></div>

            <!-- Overview -->
            <div
                class="route-overview"
                v-if="hasRoute && route.duration && !mobileSearch"
            >
                <div>
                    <div class="duration-length">
                        {{ route.duration }}
                        <span v-if="route.milesLength"
                            >({{ route.milesLength }} mi)</span
                        >
                    </div>
                    <div class="eta" v-if="route.arrival">
                        ETA: {{ route.arrival }}
                    </div>
                </div>
                <div v-if="isMobile">
                    <Button
                        @click.native="printDirections()"
                        :type="'seamless'"
                        class="blue"
                    >
                        PRINT DIRECTIONS
                    </Button>
                </div>
            </div>
        </div>

        <!-- Itinerary -->
        <div
            class="route-itinerary"
            v-show="hasRoute && route.itinerary && !isMobile"
        >
            <ol ref="itineraryList">
                <li
                    v-for="(single, index) in route.itinerary"
                    :key="index"
                    v-html="single"
                ></li>
            </ol>
            <div class="text-center">
                <Button
                    @click.native="printDirections()"
                    :type="'seamless'"
                    class="blue"
                >
                    PRINT DIRECTIONS
                </Button>
            </div>
        </div>

        <route-share ref="routeShare" :link="shareLink"></route-share>

        <truck-presets
            ref="presets"
            :options="options.truck_preset"
            :selected="route.truck_preset"
            @selectPreset="selectPreset"
        ></truck-presets>
    </div>
</template>

<script>
import TruckPresets from './TruckPresets.vue';

export default {
    components: {
        TruckPresets,
    },
    props: [
        'icons',
        'defaultRoute',
        'mobileSearch',
        'showDepartureDatepicker',
        'error',
        'options',
        'center',
        'overview',
        'loading',
    ],
    computed: {
        hasRoute() {
            return this.$route.query.guid;
        },
        waypointSuggestions() {
            const { waypoints } = this.route;
            if (waypoints[this.waypointIndex]) {
                const offset = 100;
                const position = this.waypointIndex + 1;
                const plus = 50 * position;

                return {
                    position: offset + plus,
                    suggestions: this.suggestionsWaypoint,
                };
            }
            return false;
        },
        showAddDestination() {
            const origin = this.route.origin.coordinates;
            const destination = this.route.destination.coordinates;
            return origin && destination;
        },
        downloadBannerVisible() {
            return this.$parent.$parent.$parent.downloadBanner;
        },
    },
    data() {
        return {
            route: this.defaultRoute,
            waypointIndex: null,
            waypointTimeout: null,
            suggestionsWaypoint: [],
            shareLink: '',
            hoveredSuggestion: {
                key: -1,
                value: null,
                property: null,
            },
        };
    },
    watch: {
        route: {
            handler(value) {
                if (value.shareLink) {
                    this.shareLink = value.shareLink;
                }
                this.$emit('updateRoute', value);
            },
            deep: true,
        },
        // eslint-disable-next-line
        'route.origin.name'() {
            if (this.route.block.origin) {
                this.route.block.origin = false;
                return;
            }

            this.triggerAutocomplete('origin');
        },
        // eslint-disable-next-line
        'route.destination.name'() {
            if (this.route.block.destination) {
                this.route.block.destination = false;
                return;
            }

            this.triggerAutocomplete('destination');
        },
    },
    methods: {
        waypointsAutocomplete(index) {
            const value = this.route.waypoints[index].name;
            clearTimeout(this.waypointTimeout);
            this.waypointTimeout = setTimeout(() => {
                axios
                    .get(`/api/v1/autocomplete/query/${value}/false/false`)
                    .then(({ data }) => {
                        this.suggestionsWaypoint = data;
                    })
                    .catch(() => {
                        this.$emit(
                            'showError',
                            'Something went wrong. Try again',
                        );
                    });
            }, 600);
        },
        showPresets(event) {
            this.$refs.presets.show();
            this.trackGoogle('TRUCK_PRESETS_OPEN');
            event.stopPropagation();
        },
        showSuggestions(property) {
            if (property === 'origin') {
                if (this.route.timeout.showOrigin) {
                    clearTimeout(this.route.timeout.showOrigin);
                }
                this.route.timeout.showOrigin = setTimeout(() => {
                    this.route.suggestions.showOrigin = true;
                    if (this.isMobile) {
                        this.$emit('setMobileSearch', 'Origin');
                    }
                }, 300);
            } else {
                if (this.route.timeout.showDestination) {
                    clearTimeout(this.route.timeout.showDestination);
                }
                this.route.timeout.showDestination = setTimeout(() => {
                    this.route.suggestions.showDestination = true;
                    if (this.isMobile) {
                        this.$emit('setMobileSearch', 'Destination');
                    }
                }, 300);
            }
        },
        hideSuggestions(property, onMobile = false) {
            if (property === 'origin') {
                if (this.route.timeout.showOrigin) {
                    clearTimeout(this.route.timeout.showOrigin);
                }
                this.route.timeout.showOrigin = setTimeout(() => {
                    if (!this.isMobile || (this.isMobile && onMobile)) {
                        this.route.suggestions.showOrigin = false;
                    }
                }, 300);
            } else {
                if (this.route.timeout.showDestination) {
                    clearTimeout(this.route.timeout.showDestination);
                }
                this.route.timeout.showDestination = setTimeout(() => {
                    if (!this.isMobile || (this.isMobile && onMobile)) {
                        this.route.suggestions.showDestination = false;
                    }
                }, 300);
            }
            this.hoveredSuggestion = {
                key: -1,
                property: null,
                value: null,
            };
        },
        pickSuggestion(suggestion, property, waypointIndex = null) {
            if (waypointIndex !== null) {
                this.route.waypoints[waypointIndex] = {
                    name: suggestion.label,
                    full: suggestion.label,
                    id: suggestion.locationId,
                    coordinates: null,
                };
                this.$emit('updateWaypoint', waypointIndex);
                this.suggestionsWaypoint = [];
            } else {
                this.route[property] = {
                    name: `${suggestion.label}`,
                    full: suggestion.label,
                    id: suggestion.locationId,
                    coordinates: null,
                };
                this.route.block[property] = true;
                this.route.suggestions[property] = [];
                this.findSearch(this.route[property], property);
            }
            if (this.isMobile) {
                this.$emit('setMobileSearch', null);
            }

            this.trackGoogle('PLAN_SEARCH_SELECT');
        },
        findSearch(query, property) {
            this.$emit('findSearch', query, property);
        },
        swapOriginDestination() {
            const original = this.route;

            this.route = Object.assign(this.route, {
                origin: original.destination,
                destination: original.origin,
            });

            this.$emit('calculateRoute');
        },
        triggerAutocomplete(property, waypointIndex = null) {
            clearTimeout(this.route.timeout[property]);
            if (waypointIndex !== null) {
                this.route.timeout[property] = setTimeout(() => {
                    this.showAutocomplete(
                        this.route.waypoints[waypointIndex].name,
                        property,
                    );
                }, 600);
            } else {
                this.route.timeout[property] = setTimeout(() => {
                    this.showAutocomplete(this.route[property].name, property);
                }, 600);
            }
        },
        showAutocomplete(query, property) {
            if (query) {
                axios
                    .get(
                        `/api/v1/autocomplete/query/${query}/${this.center.lat}/${this.center.lng}`,
                    )
                    .then(({ data }) => {
                        this.route.suggestions[property] = data;
                    })
                    .catch(() => {
                        this.$emit(
                            'showError',
                            'Something went wrong. Try again',
                        );
                    });
            } else {
                this.route.suggestions[property] = [];
            }
        },
        opensidebar() {
            this.$emit('opensidebar');
        },
        selectPreset(preset) {
            this.route.truck_preset = preset;

            axios.post('/api/v1/truck-preset/select', {
                session: window.config.session,
                selected: preset,
            });
        },
        currentLocationToRoutePlan(property) {
            this.$emit('currentLocationToRoutePlan', property);
        },
        showRouteShare() {
            if (!this.route.shareLink) {
                let waypoints = '';
                if (this.route.waypoints.length) {
                    this.route.waypoints.forEach((single) => {
                        waypoints += `${single.coordinates.lat},${single.coordinates.lng}/`;
                    });
                }
                const origin = `${this.route.origin.coordinates.lat},${this.route.origin.coordinates.lng}`;
                const destination = `${this.route.destination.coordinates.lat},${this.route.destination.coordinates.lng}`;
                const link = `${window.location.origin}/app/route/${origin}/${waypoints}${destination}`;
                this.shareLink = link;
                this.$refs.routeShare.show();
            } else {
                this.$refs.routeShare.show();
            }
            this.trackGoogle('PLAN_SHARE');
        },
        sendToDevice() {
            console.log(this.route);
        },
        removeWaypoint(index) {
            this.$emit('removeMarker', index);
            this.route.waypoints.splice(index, 1);
            this.$emit('calculateRoute');
        },
        trackGoogle(event) {
            this.$gtag.event(event, {
                event_category: 'PLAN',
            });
        },
        listSuggestions(property, direction) {
            const suggestions = this.route.suggestions[property];
            if (suggestions.length) {
                const key = this.hoveredSuggestion.key + direction;
                if (direction === 1 && key < suggestions.length) {
                    this.hoveredSuggestion.key = key;
                    this.hoveredSuggestion.value = suggestions[key];
                    this.hoveredSuggestion.property = property;
                } else if (direction === -1 && key >= 0) {
                    this.hoveredSuggestion.key = key;
                    this.hoveredSuggestion.value = suggestions[key];
                    this.hoveredSuggestion.property = property;
                }
            }
        },
        pickSuggestionFromList(property) {
            const hProperty = this.hoveredSuggestion.property;
            const { value } = this.hoveredSuggestion;
            if (hProperty === property && value) {
                this.pickSuggestion(value, property);
            }
        },
        printDirections() {
            const newWindow = window.open();
            newWindow.document.write(`
                <link rel="preconnect" href="https://fonts.gstatic.com">
                <link href="https://fonts.googleapis.com/css2?family=Barlow+Semi+Condensed:wght@300;400;500;600&display=swap" rel="stylesheet">
                <style>
                    * {
                        font-family: 'Barlow Semi Condensed';
                        color: #000;
                    }

                    li {
                        margin-bottom: 5px;
                    }

                    span:not(.distance-description) {
                        font-weight: bold;
                    }

                    .distance-description {
                        display: block;
                    }
                </style>
                <ol>${this.$refs.itineraryList.innerHTML}</ol>
            `);
            newWindow.document.close();
            newWindow.focus();
            newWindow.print();
            newWindow.onafterprint = () => {
                newWindow.close();
            };
            this.trackGoogle('PLAN_PRINT');
        },
        addNewDestination() {
            this.$emit('addNewDestination');
        },
    },
};
</script>
<style lang="scss">
.loading-indicator {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 99;
    font-weight: 500;
    font-size: 18px;

    .lds-ring {
        display: inline-block;
        position: relative;
        width: 60px;
        height: 60px;
    }
    .lds-ring div {
        box-sizing: border-box;
        display: block;
        position: absolute;
        width: 44px;
        height: 44px;
        margin: 4px;
        border: 4px solid #fff;
        border-radius: 50%;
        animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
        border-color: #fff transparent transparent transparent;
    }
    .lds-ring div:nth-child(1) {
        animation-delay: -0.45s;
    }
    .lds-ring div:nth-child(2) {
        animation-delay: -0.3s;
    }
    .lds-ring div:nth-child(3) {
        animation-delay: -0.15s;
    }
    @keyframes lds-ring {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
}
.route-overview {
    padding: 20px 16px;
    box-shadow: inset 0px 5px 10px 0px rgba(0, 0, 0, 0.1);

    .duration-length {
        color: #4e3a30;
        font-weight: 700;
        font-size: 20px;
    }

    .eta {
        font-size: 14px;
        color: rgba(78, 58, 48, 0.6);
    }

    @media screen and (max-width: 768px) {
        position: absolute;
        height: 100px;
        display: flex;
        // flex-direction: column;
        justify-content: space-between;
        align-items: center;
        background-color: #fff;
        left: 0;
        right: 0;
        top: calc(var(--vh, 1vh) * 100 - 100px);
    }
}

.route-itinerary {
    overflow-y: auto;

    ol {
        color: #4e3a30;
        padding-left: 32px;
        padding-right: 10px;
        margin-bottom: 25px;

        li {
            margin-bottom: 5px;

            span:not(.distance-description) {
                font-weight: bold;
            }

            .distance-description {
                display: block;
            }
        }
    }

    button {
        margin-bottom: 25px;
    }
}
</style>
